import type { SyncProvider } from './syncProvider.js';
import { storage } from '../shell/storage.js';

/**
 * F2 — multi-user shared module data (port of Kotlin SharedSync).
 *
 * A module's data can be bound to a VISIBLE shared Drive folder (invites =
 * normal Drive folder sharing). Shared namespaces have 2+ concurrent writers,
 * so whole-snapshot LWW would silently drop other people's edits. This pass
 * merges at the RECORD level instead:
 *
 *   - every key carries {value, ts}; merge = union of records
 *   - same-key conflict → per-record LWW on ts
 *   - deletions become TOMBSTONES ({ts, deleted:true}) so a delete can't be
 *     resurrected by another member's stale copy; tombstones older than
 *     TOMBSTONE_TTL_MS are pruned on push
 *   - "deleted locally" is detected via the binding's knownKeys — the keys
 *     that existed locally after the last merge — not clock baselines, so it
 *     works across devices with different clocks
 *
 * The private sync for the same module keeps running unchanged.
 */

const BINDINGS_KEY = 'shell.sync.sharedBindings';
const TOMBSTONE_TTL_MS = 30 * 24 * 3600 * 1000; // 30 days

export interface SharedBinding {
  folderId: string;
  folderName: string;
  link: string;
  /** Keys present locally at the end of the last merge — the local-delete detector. */
  knownKeys: string[];
  lastMergedMs: number;
}

interface SharedRecord {
  value?: string;
  ts: number;
  deleted?: boolean;
}

interface SharedDoc {
  v: number;
  moduleId: string;
  lastModifiedMs: number;
  by: string;
  records: Record<string, SharedRecord>;
}

export interface SharedOutcome {
  uploaded: number;
  downloaded: number;
  deleted: number;
  pushed: boolean;
}

// ─── Bindings store ──────────────────────────────────────────────────────────

export function getSharedBindings(): Record<string, SharedBinding> {
  try {
    return JSON.parse(localStorage.getItem(BINDINGS_KEY) || '{}') as Record<string, SharedBinding>;
  } catch {
    return {};
  }
}

export function saveSharedBindings(all: Record<string, SharedBinding>): void {
  try { localStorage.setItem(BINDINGS_KEY, JSON.stringify(all)); } catch { /* quota */ }
}

export function bindSharedModule(moduleId: string, binding: SharedBinding): void {
  const all = getSharedBindings();
  all[moduleId] = binding;
  saveSharedBindings(all);
}

export function unbindSharedModule(moduleId: string): boolean {
  const all = getSharedBindings();
  if (!(moduleId in all)) return false;
  delete all[moduleId];
  saveSharedBindings(all);
  return true;
}

export function hasSharedBindings(): boolean {
  return Object.keys(getSharedBindings()).length > 0;
}

// ─── The merge pass ──────────────────────────────────────────────────────────

export function sharedFileName(moduleId: string): string {
  return `${moduleId}.json`;
}

/**
 * Reconciles one binding. May throw — the engine catches per-binding so one
 * bad folder can't fail the others. `skipCache` uses the same
 * "<remoteModifiedMs>|<localSignal>" trick as the private sync, so an idle
 * binding costs one folder listing and nothing else.
 */
export async function syncSharedModule(
  provider: SyncProvider,
  moduleId: string,
  binding: SharedBinding,
  skipCache: Record<string, string>,
  deviceId: string,
): Promise<SharedOutcome> {
  const fileName = sharedFileName(moduleId);
  const listing = await provider.listSharedFolder?.(binding.folderId);
  if (!listing) throw new Error('Shared folder unavailable (scope or access lost)');
  const remoteMeta = listing.find((f) => f.path === fileName);

  const localSig = localDataSignal(moduleId);
  const sig = `${remoteMeta?.remoteModifiedMs ?? 0}|${localSig}`;
  if (skipCache[`s:${moduleId}`] === sig) {
    return { uploaded: 0, downloaded: 0, deleted: 0, pushed: false };
  }

  const localMeta = storage.getMeta(moduleId);
  const localKeys = collectLocalKeys(moduleId, localMeta);

  // If the shared file exists we MUST have its contents before merging —
  // merging against an empty doc after a failed read would push a doc built
  // from local keys only, clobbering other members' records.
  let records: Record<string, SharedRecord> = {};
  if (remoteMeta) {
    const json = await provider.readSharedFile?.(binding.folderId, fileName);
    const doc = json ? parseDoc(json) : null;
    if (!doc) throw new Error('Shared file unreadable — skipped to protect other members\' data');
    records = doc.records;
  }
  const hadRemoteDoc = !!remoteMeta;

  let uploaded = 0;
  let downloaded = 0;
  let deleted = 0;
  const now = Date.now();
  const known = new Set(binding.knownKeys);
  const toWriteLocal: Record<string, SharedRecord> = {};
  const toDeleteLocal: string[] = [];

  const allKeys = new Set([...Object.keys(localKeys), ...Object.keys(records)]);
  for (const key of allKeys) {
    const l = localKeys[key];
    const r = records[key];
    if (l && r) {
      if (r.deleted) {
        if (l.ts > r.ts) {
          // Re-created locally after someone's delete — undelete.
          records[key] = { value: l.value, ts: l.ts }; uploaded++;
        } else {
          toDeleteLocal.push(key); deleted++;
        }
      } else if (l.ts > r.ts) {
        records[key] = { value: l.value, ts: l.ts }; uploaded++;
      } else if (r.ts > l.ts) {
        toWriteLocal[key] = r; downloaded++;
      }
    } else if (l) {
      // Not in the shared doc at all — new local key, push it.
      records[key] = { value: l.value, ts: l.ts }; uploaded++;
    } else if (r) {
      if (r.deleted) {
        // Already gone locally too.
      } else if (known.has(key)) {
        // We had it after the last merge and deleted it since — tombstone.
        records[key] = { ts: now, deleted: true }; deleted++; uploaded++;
      } else {
        toWriteLocal[key] = r; downloaded++;
      }
    }
  }

  // Apply pulls (+ authoritative remote ts, matching the private pull path).
  for (const [key, rec] of Object.entries(toWriteLocal)) {
    storage.set({ moduleId, key }, rec.value ?? '');
    storage.setMetaTimestamp(moduleId, key, rec.ts);
  }
  for (const key of toDeleteLocal) {
    storage.delete({ moduleId, key });
  }

  // Prune expired tombstones, then push if our merge changed the doc.
  const beforePrune = Object.keys(records).length;
  for (const [key, rec] of Object.entries(records)) {
    if (rec.deleted && now - rec.ts > TOMBSTONE_TTL_MS) delete records[key];
  }
  const needsPush = uploaded > 0 || Object.keys(records).length !== beforePrune || !hadRemoteDoc;
  let pushed = false;
  if (needsPush) {
    const doc: SharedDoc = { v: 1, moduleId, lastModifiedMs: now, by: deviceId, records };
    pushed = (await provider.writeSharedFile?.(binding.folderId, fileName, JSON.stringify(doc))) ?? false;
  }

  // Refresh binding bookkeeping + skip cache.
  binding.knownKeys = Object.keys(collectLocalKeys(moduleId, storage.getMeta(moduleId)));
  binding.lastMergedMs = now;
  if (pushed || !remoteMeta) delete skipCache[`s:${moduleId}`];
  else skipCache[`s:${moduleId}`] = `${remoteMeta.remoteModifiedMs}|${localDataSignal(moduleId)}`;

  return { uploaded, downloaded, deleted, pushed };
}

// ─── Local helpers (mirror syncEngine's private ones) ────────────────────────

function collectLocalKeys(moduleId: string, meta: Record<string, number>): Record<string, { value: string; ts: number }> {
  const out: Record<string, { value: string; ts: number }> = {};
  for (const key of storage.listKeysForModule(moduleId)) {
    if (key.startsWith('__published__::') || key.startsWith('.')) continue;
    out[key] = {
      value: storage.get({ moduleId, key }),
      ts: meta[key] ?? Date.now(),
    };
  }
  return out;
}

function localDataSignal(moduleId: string): string {
  const meta = storage.getMeta(moduleId);
  let count = 0;
  let max = 0;
  for (const [k, ts] of Object.entries(meta)) {
    if (k.startsWith('__published__::') || k.startsWith('.')) continue;
    count++;
    if (ts > max) max = ts;
  }
  return `${count}:${max}`;
}

function parseDoc(json: string): SharedDoc | null {
  try {
    const doc = JSON.parse(json) as SharedDoc;
    if (!doc || typeof doc !== 'object') return null;
    if (!doc.records) return { ...doc, records: {} };
    return doc;
  } catch {
    return null;
  }
}
